import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { modalBackdropCls, modalCardCls, overlayCls } from '../../overlay';

// Authorize a Google account by email. The address does not have to exist in
// NoticeIA yet: the row is created empty and claimed on that account's first
// sign-in. Styled to match CategoryFormModal.
export function AddUserModal({
  onAdd,
  onClose,
}: {
  // Resolves to an error message key, or null when the user was added.
  onAdd: (email: string) => Promise<string | null>;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    const value = email.trim();
    if (!value || saving) return;
    setSaving(true);
    setError(null);
    const failure = await onAdd(value);
    if (failure) {
      setError(failure);
      setSaving(false);
      return;
    }
    onClose();
  };

  return (
    <div
      className={`${overlayCls} ${modalBackdropCls}`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-sm p-5 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-lg font-bold text-slate-900">
          {t('notice.users.addTitle')}
        </h2>
        <p className="mb-4 text-xs text-slate-500">{t('notice.users.addHint')}</p>

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('notice.users.email')}
        </label>
        <input
          autoFocus
          type="email"
          value={email}
          placeholder="nom@exemple.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void submit()}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500"
        />
        {error && <p className="mt-2 text-xs text-rose-600">{t(error)}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-100"
          >
            {t('notice.catalog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!email.trim() || saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('notice.users.addSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
